import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import BiBudgetDashboard from './BiBudgetDashboard';
import OverviewDashboard from './OverviewDashboard';
import FunnelAnalysis from './FunnelAnalysis';
import PromoTracker from './PromoTracker';
import BiCalendar from './BiCalendar';
import BaReportGenerator from './BaReportGenerator';
import { useBiVendorId } from './useBiVendorId';

const BiHub = () => {
  const { hasFeatureAccess } = useAuthStore();
  const { storeLabel } = useBiVendorId();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const biTabs = useMemo(() => {
    const canAccessHub = hasFeatureAccess('bi.hub');
    const canBudget = canAccessHub && (hasFeatureAccess('bi.budget') || hasFeatureAccess('bi.overview'));
    const canOverview = canAccessHub && hasFeatureAccess('bi.overview');
    const canFunnel = canAccessHub && hasFeatureAccess('bi.funnel');
    const canPromo = canAccessHub && hasFeatureAccess('bi.promo');
    const canBa = canAccessHub && hasFeatureAccess('bi.ba_report');
    const canCalendar = canAccessHub && hasFeatureAccess('bi.calendar');

    return [
      ...(canBudget ? [{ key: 'budget', label: 'Budget vs Actuals', component: BiBudgetDashboard }] : []),
      ...(canOverview ? [{ key: 'overview', label: 'Overview', component: OverviewDashboard }] : []),
      ...(canFunnel ? [{ key: 'funnel', label: 'Funnel', component: FunnelAnalysis }] : []),
      ...(canPromo ? [{ key: 'promo', label: 'Promo Deals', component: PromoTracker }] : []),
      ...(canBa ? [{ key: 'ba-report', label: 'BA Report', component: BaReportGenerator }] : []),
      ...(canCalendar ? [{ key: 'calendar', label: 'Calendar', component: BiCalendar }] : []),
    ];
  }, [hasFeatureAccess]);

  const routePreferredTab = useMemo(() => {
    const path = location.pathname;
    if (path === '/bi/budget') return 'budget';
    if (path === '/bi/overview') return 'overview';
    if (path === '/bi/funnel') return 'funnel';
    if (path === '/bi/promo') return 'promo';
    if (path === '/bi/calendar') return 'calendar';
    if (path === '/bi/ba-report') return 'ba-report';
    return '';
  }, [location.pathname]);

  const activeTab = searchParams.get('tab') || routePreferredTab || biTabs[0]?.key || '';

  useEffect(() => {
    if (!biTabs.length) return;
    if (!biTabs.some((tab) => tab.key === activeTab)) {
      setSearchParams({ tab: biTabs[0].key }, { replace: true });
    }
  }, [activeTab, biTabs, setSearchParams]);

  const selectedTab = biTabs.find((tab) => tab.key === activeTab) || biTabs[0];
  const [visitedTabs, setVisitedTabs] = useState(() => new Set(selectedTab ? [selectedTab.key] : []));

  useEffect(() => {
    if (!selectedTab?.key) return;
    setVisitedTabs((current) => {
      if (current.has(selectedTab.key)) return current;
      const next = new Set(current);
      next.add(selectedTab.key);
      return next;
    });
  }, [selectedTab?.key]);

  if (!biTabs.length) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-500">Access denied</span>
          <span className="text-lg font-bold tracking-tight text-slate-900">BI</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {biTabs.map((tab) => {
            const isActive = tab.key === selectedTab?.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setSearchParams({ tab: tab.key })}
                className={`rounded-lg px-3.5 py-1.5 text-sm font-semibold transition ${
                  isActive
                    ? 'bg-[linear-gradient(135deg,#dbeafe_0%,#bfdbfe_38%,#ccfbf1_100%)] text-sky-900 shadow-lg shadow-sky-200/60 ring-1 ring-sky-200'
                    : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-800">
            {storeLabel}
          </span>
          <span className="text-lg font-bold tracking-tight text-slate-900">BI</span>
        </div>
      </div>

      {biTabs.map((tab) => {
        if (!visitedTabs.has(tab.key)) return null;
        const TabComponent = tab.component;
        const isActive = tab.key === selectedTab?.key;
        return (
          <div key={tab.key} className={isActive ? 'block' : 'hidden'}>
            <TabComponent />
          </div>
        );
      })}
    </div>
  );
};

export default BiHub;
